import { normalizeTier, type PlanTier } from '@/lib/planLimits';
import { isDemoInstitutionUser } from '@/lib/demo-content';
import type { InstitutionMetrics } from '@/lib/institution-metrics';

export type InstitutionWorkspaceRole = 'Owner' | 'Admin' | 'Verifier' | 'Archivist' | 'Member';

export type InstitutionSetupItemId =
  | 'logo'
  | 'public_profile'
  | 'program'
  | 'record_template'
  | 'reviewer'
  | 'participant'
  | 'submission_intake'
  | 'public_record'
  | 'archive_collection';

export type InstitutionSetupItem = {
  id: InstitutionSetupItemId;
  label: string;
  href: string;
};

export type InstitutionSetupProgress = Record<InstitutionSetupItemId, boolean>;

export const INSTITUTION_SETUP_ITEMS: InstitutionSetupItem[] = [
  { id: 'logo', label: 'Add institution logo', href: '/institution-dashboard/settings' },
  {
    id: 'public_profile',
    label: 'Complete institution profile',
    href: '/institution-dashboard/settings',
  },
  {
    id: 'program',
    label: 'Create a program',
    href: '/institution-dashboard?section=programs',
  },
  {
    id: 'record_template',
    label: 'Create a record template',
    href: '/institution-dashboard?section=record-templates',
  },
  {
    id: 'reviewer',
    label: 'Invite a reviewer',
    href: '/institution-dashboard?section=people',
  },
  {
    id: 'participant',
    label: 'Add participants',
    href: '/institution-dashboard?section=participants',
  },
  {
    id: 'submission_intake',
    label: 'Configure submission intake',
    href: '/institution-dashboard?section=publish-center',
  },
  {
    id: 'public_record',
    label: 'Publish first public record',
    href: '/institution-dashboard?section=public-trust-pages',
  },
  {
    id: 'archive_collection',
    label: 'Create first archive collection',
    href: '/institution-dashboard?section=heritage-archive',
  },
];

type InstitutionSetupProfile = {
  avatar_url?: string | null;
  institution_name?: string | null;
  full_name?: string | null;
  bio?: string | null;
  public_handle?: string | null;
};

type InstitutionSetupMetrics = Pick<
  InstitutionMetrics,
  | 'activePrograms'
  | 'recordTemplates'
  | 'activeReviewers'
  | 'participants'
  | 'publicRecords'
  | 'archiveCollections'
>;

export function deriveInstitutionSetupProgress(
  profile: InstitutionSetupProfile | null,
  metrics: InstitutionSetupMetrics,
  intakeConfigured: boolean
): InstitutionSetupProgress {
  const institutionName = profile?.institution_name?.trim() || profile?.full_name?.trim();

  return {
    logo: Boolean(profile?.avatar_url?.trim()),
    public_profile: Boolean(
      institutionName && profile?.bio?.trim() && profile.public_handle?.trim()
    ),
    program: metrics.activePrograms > 0,
    record_template: metrics.recordTemplates > 0,
    reviewer: metrics.activeReviewers > 0,
    participant: metrics.participants > 0,
    submission_intake: intakeConfigured,
    public_record: metrics.publicRecords > 0,
    archive_collection: metrics.archiveCollections > 0,
  };
}

export function formatInstitutionPlanBadge(
  tier: string | null | undefined,
  options?: { isPresentationDemo?: boolean; email?: string | null }
): string {
  if (options?.isPresentationDemo || isDemoInstitutionUser(options?.email)) {
    return 'Starter';
  }
  const normalized: PlanTier = normalizeTier(tier);
  switch (normalized) {
    case 'collective_free':
      return 'Free';
    case 'collective_verified':
      return 'Verified';
    case 'collective_pro':
      return 'Pro';
    case 'institution_starter':
      return 'Starter';
    case 'institution_growth':
      return 'Growth';
    case 'vaas':
      return 'Enterprise';
    case 'free':
      return 'Free';
    default:
      return 'Trial';
  }
}

export function formatInstitutionMemberRole(
  dbRole: string | null | undefined,
  options: { userId: string; institutionOwnerId: string; profileRole?: string | null }
): InstitutionWorkspaceRole {
  if (options.userId === options.institutionOwnerId || options.profileRole === 'institution') {
    return 'Owner';
  }
  switch (dbRole) {
    case 'admin':
      return 'Admin';
    case 'verifier':
      return 'Verifier';
    case 'archivist':
      return 'Archivist';
    case 'viewer':
      return 'Member';
    default:
      return 'Member';
  }
}

export function resolveInstitutionDisplayId(
  institutionId: string | null | undefined,
  profileId: string | null | undefined
): string {
  if (institutionId?.trim()) return institutionId.trim();
  if (profileId) return `INST-${profileId.slice(0, 8).toUpperCase()}`;
  return 'INST-PENDING';
}

export function institutionNeedsSetup(
  profile: {
    institution_name?: string | null;
    full_name?: string | null;
    institution_id?: string | null;
  } | null
): boolean {
  if (!profile) return true;
  const name = profile.institution_name || profile.full_name;
  return !name?.trim() || !profile.institution_id?.trim();
}
